import React from 'react';
import styles from './FeaturesSection.module.css';
import { trackCtaClick } from '../../../../utils/analytics';
import OwnNumberNote from '../shared/OwnNumberNote';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DevicesIcon from '@mui/icons-material/Devices';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';

// Ícone de check idêntico ao usado na Hero Section
const HighlightCheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="8" fill="#5a80fb" fillOpacity="0.12"/>
    <path d="M4.5 8L7 10.5L11.5 6" stroke="#5a80fb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const iconMap = {
  CalendarMonth: CalendarMonthIcon,
  WhatsApp: WhatsAppIcon,
  FolderShared: FolderSharedIcon,
  BarChart: BarChartIcon,
  Description: DescriptionIcon,
  AdminPanelSettings: AdminPanelSettingsIcon,
  Receipt: ReceiptIcon,
  Devices: DevicesIcon,
  EventAvailable: EventAvailableIcon,
  EventAvailableOutlined: EventAvailableOutlinedIcon,
  LocalOfferOutlined: LocalOfferOutlinedIcon,
  CampaignOutlined: CampaignOutlinedIcon,
};

const features = [
  {
    id: 1,
    badge: "Principal",
    badgeColor: "#e6f2ff",
    badgeTextColor: "#1a5490",
    icon: "CalendarMonth",
    iconBg: "#e8eeff",
    iconColor: "#4f6ef7",
    title: "Agenda organizada",
    description: "Todos os atendimentos em um só calendário, com marcação e remarcação em poucos cliques. Você enxerga o dia inteiro de uma vez e nunca marca duas pessoas no mesmo horário.",
    highlights: ["Agendamento e reagendamento rápidos", "Sem conflito de horários ou de salas", "Visão do dia e da semana"],
    image: "/assets/features/feature-1.png",
  },
  {
    id: 2,
    badge: "Comunicação",
    badgeColor: "#fff0f6",
    badgeTextColor: "#c2185b",
    icon: "WhatsApp",
    iconBg: "#e8f5e9",
    iconColor: "#2e7d32",
    title: "Confirmações e lembretes",
    description: "Antes de cada atendimento, a Gabriela deixa a confirmação e o lembrete prontos para enviar pelo WhatsApp em um clique, ou envia sozinha*. Você para de digitar mensagem uma por uma e sabe com antecedência quem não vem.",
    highlights: ["Confirmações com a mensagem pronta", "Lembretes antes do atendimento", "Menos faltas e menos tempo no celular"],
    image: "/assets/features/feature-5.png",
  },
  {
    id: 3,
    badge: "Histórico",
    badgeColor: "#f0fff4",
    badgeTextColor: "#276749",
    icon: "FolderShared",
    iconBg: "#e8f5e9",
    iconColor: "#388e3c",
    title: "Prontuário digital",
    description: "Registre a evolução de cada atendimento e encontre o histórico completo do paciente na hora, sem pastas de papel nem arquivos espalhados.",
    highlights: ["Evolução por atendimento", "Histórico completo em segundos", "Acesso seguro de qualquer lugar"],
    image: "/assets/features/feature-3.png",
  },
  {
    id: 4,
    badge: "Estatísticas",
    badgeColor: "#f9f6ff",
    badgeTextColor: "#6b21a8",
    icon: "BarChart",
    iconBg: "#ede9fe",
    iconColor: "#7c3aed",
    title: "Financeiro e indicadores",
    description: "Acompanhe o que entrou, o que está pendente e como o seu trabalho está evoluindo, com indicadores de atendimentos, faturamento, perfil dos pacientes e satisfação.",
    highlights: ["Faturamento e pagamentos", "Dados demográficos dos pacientes", "Pesquisa de satisfação (NPS)"],
    image: "/assets/features/feature-7.png",
  },
  {
    id: 5,
    badge: "Documentos",
    badgeColor: "#fff8f0",
    badgeTextColor: "#c05621",
    icon: "Description",
    iconBg: "#fff3e0",
    iconColor: "#e65100",
    title: "Documentos rápidos",
    description: "Atestados e relatórios gerados a partir de modelos, já preenchidos com os dados do paciente. Você revisa, emite e o documento fica guardado no cadastro.",
    highlights: ["Modelos prontos e personalizáveis", "Preenchimento automático", "Arquivado no cadastro do paciente"],
    image: "/assets/features/feature-4.png",
  },
  {
    id: 6,
    badge: "Controle",
    badgeColor: "#f8f0ff",
    badgeTextColor: "#6b21a8",
    icon: "AdminPanelSettings",
    iconBg: "#fce4ec",
    iconColor: "#c2185b",
    title: "Permissões e acessos",
    description: "Defina o que cada pessoa da equipe pode ver e fazer. Equipe, profissionais de saúde e gestão trabalham no mesmo sistema, cada um com o acesso certo.",
    highlights: ["Perfis por função", "Equipe inteira no mesmo sistema", "Dados sensíveis protegidos"],
    image: "/assets/features/feature-6.png",
  },
  {
    id: 7,
    badge: "Financeiro",
    badgeColor: "#fff8f0",
    badgeTextColor: "#c05621",
    icon: "Receipt",
    iconBg: "#e3f2fd",
    iconColor: "#1565c0",
    title: "NFS-e integrada",
    description: "Emita a nota fiscal de serviço direto da Gabriela, sem digitar tudo de novo no site da prefeitura. Os dados do paciente e do atendimento já vêm preenchidos.",
    highlights: ["Emissão em poucos cliques", "Dados já preenchidos", "Integração com Focus NFe"],
    image: "/assets/features/feature-nf.png",
  },
  {
    id: 9,
    badge: "Otimização",
    badgeColor: "#f0fdf4",
    badgeTextColor: "#166534",
    icon: "EventAvailable",
    iconBg: "#dcfce7",
    iconColor: "#16a34a",
    title: "Lista de espera",
    description: "Quando alguém cancela, a Gabriela mostra quem está esperando por um horário e ajuda você a preencher a vaga rapidamente, em vez de deixar o horário vazio.",
    highlights: ["Pacientes aguardando vaga", "Cancelamento preenchido rápido", "Agenda mais cheia"],
    text: "Quando um paciente cancela, a Gabriela ajuda você a ocupar rapidamente aquele horário com a lista de espera.",
    cta: "Reduzir horários vagos",
    image: "/assets/features/feature-2.png",
  },
  {
    id: 8,
    badge: "Mobilidade",
    badgeColor: "#e6f2ff",
    badgeTextColor: "#1a5490",
    icon: "Devices",
    iconBg: "#e8eeff",
    iconColor: "#4f6ef7",
    title: "Tudo sincronizado",
    description: "Acesse do computador, do tablet ou do celular. Tudo o que você registra fica salvo na nuvem e disponível para toda a equipe na hora.",
    highlights: ["Funciona no navegador", "Nada para instalar", "Mesma informação para toda a equipe"],
    image: "/assets/features/feature-devices.png",
  },
  {
    id: 10,
    badge: "Pacientes",
    badgeColor: "#fff0f6",
    badgeTextColor: "#c2185b",
    icon: "EventAvailableOutlined",
    iconBg: "#fce4ec",
    iconColor: "#c2185b",
    title: "Agendamento online",
    description: "Compartilhe um link onde o paciente escolhe profissional, serviço e horário e envia o pedido pelo WhatsApp. Pelo link de confirmação, ele confirma presença, pede reagendamento ou cancela sem precisar te ligar.",
    highlights: ["Link de agendamento para compartilhar", "Paciente escolhe serviço e horário", "Confirmação, reagendamento e cancelamento pelo link"],
    image: "/assets/features/feature-agenda-publica.png",
  },
  {
    id: 11,
    badge: "Comercial",
    badgeColor: "#fff8f0",
    badgeTextColor: "#c05621",
    icon: "LocalOfferOutlined",
    iconBg: "#fff3e0",
    iconColor: "#e65100",
    title: "Serviços, pacotes e orçamentos",
    description: "Cadastre seus serviços com preço e duração, monte pacotes de sessões com validade e gere orçamentos em PDF em poucos cliques, sem planilha e sem calculadora.",
    highlights: ["Catálogo de serviços com preço e duração", "Pacotes com validade e desconto", "Orçamento em PDF na hora"],
    image: "/assets/features/feature-pacotes.png",
  },
  {
    id: 12,
    badge: "Crescimento",
    badgeColor: "#f0fdf4",
    badgeTextColor: "#166534",
    icon: "CampaignOutlined",
    iconBg: "#dcfce7",
    iconColor: "#16a34a",
    title: "Leads e marketing",
    description: "Registre quem entrou em contato, de onde veio e por qual campanha, acompanhe quem virou paciente e fale com todos pelo WhatsApp, inclusive em envios em massa*.",
    highlights: ["Leads por origem e campanha", "Contato direto pelo WhatsApp", "Envio de mensagens em massa*"],
    image: "/assets/features/feature-leads.png",
  },
];

function FeatureCard({ feature }) {
  const IconComponent = iconMap[feature.icon];

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div
          className={styles.iconWrapper}
          style={{ backgroundColor: feature.iconBg }}
        >
          {IconComponent && (
            <IconComponent
              style={{ color: feature.iconColor, fontSize: 20 }}
            />
          )}
        </div>
        <div className={styles.cardText}>
          <h3 className={styles.cardTitle}>{feature.title}</h3>
          <p className={styles.cardDescription}>{feature.description}</p>
          {feature.highlights && (
            <ul className={styles.cardHighlights}>
              {feature.highlights.map((h) => (
                <li key={h} className={styles.cardHighlight}><HighlightCheckIcon /><span>{h}</span></li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {feature.image && (
        <div className={styles.cardMockup}>
          <img
            src={feature.image}
            alt={feature.title}
            className={styles.mockupImage}
          />
        </div>
      )}
    </div>
  );
}

function FeaturesSection({ onScrollToBeta }) {
  return (
    <section id="funcionalidades" className={styles.featuresSection}>

      {/* ── DESKTOP: grid original ── */}
      <div className={`${styles.sectionContainer} ${styles.featuresDesktopOnly}`}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>FUNCIONALIDADES</span>
          <h2 className={styles.sectionTitle}>
            Tudo que a sua rotina precisa,
            <br />
            <span className={styles.sectionTitleHighlight}>em um só lugar.</span>
          </h2>
          <p className={styles.sectionSubtitle}>
            Cada recurso existe para tirar uma tarefa da sua lista e deixar sua rotina sob controle.
          </p>
        </div>

        <div className={styles.featuresGrid}>
          {features.map((feature) => (
            <FeatureCard key={feature.id} feature={feature} />
          ))}
        </div>

        <OwnNumberNote className={styles.sectionNote} />

        <div className={styles.sectionCta}>
          <button
            className={styles.ctaButton}
            onClick={() => {
              trackCtaClick({ placement: 'features_section_cta' });
              onScrollToBeta();
            }}
          >
            Explorar todas as funcionalidades →
          </button>
        </div>
      </div>

      {/* ── MOBILE: lista compacta ── */}
      <div className={styles.mobileFeaturesSection}>
        <div className={styles.mobileFeaturesHeader}>
          <span className={styles.mobileFeaturesBadge}>FUNCIONALIDADES</span>
          <h2 className={styles.mobileFeaturesTitle}>
            Funcionalidades que<br /><span className={styles.mobileFeaturesHighlight}>facilitam</span> sua rotina
          </h2>
        </div>

        <div className={styles.mobileFeaturesList}>
          {features.map((feature) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div key={feature.id} className={styles.mobileFeatureItem}>
                <div className={styles.mobileFeatureItemLeft}>
                  <div
                    className={styles.mobileFeatureItemIcon}
                    style={{ background: feature.iconBg }}
                  >
                    {IconComponent && (
                      <IconComponent style={{ color: feature.iconColor, fontSize: 20 }} />
                    )}
                  </div>
                  <div className={styles.mobileFeatureItemContent}>
                    <h3 className={styles.mobileFeatureItemTitle}>{feature.title}</h3>
                    <p className={styles.mobileFeatureItemDesc}>{feature.description}</p>
                    {feature.highlights && (
                      <ul className={styles.mobileFeatureHighlights}>
                        {feature.highlights.map((h) => (
                          <li key={h} className={styles.mobileFeatureHighlight}><HighlightCheckIcon /><span>{h}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                {feature.image && (
                  <div className={styles.mobileFeatureItemImage}>
                    <img src={feature.image} alt={feature.title} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <OwnNumberNote className={styles.mobileSectionNote} />

        <button
          className={styles.mobileFeaturesCta}
          onClick={() => {
            trackCtaClick({ placement: 'features_mobile_cta' });
            onScrollToBeta();
          }}
        >
          Testar grátis por 30 dias →
        </button>
      </div>

    </section>
  );
}

export default FeaturesSection;
